<template>
  <div>
    <div
      v-if="currentList && ((compareProductList as { item_count?: number })?.item_count || 0) > 0"
      ref="compareCta"
      class="compare-cta"
    >
      <div class="compare-list__overview">
        <button
          class="compare-list__show button button--cta button--full button--center"
          role="button"
          :aria-expanded="showCompareList"
          tabindex="0"
          @click="toggleCompareList"
          @keydown.enter="toggleCompareList"
          @keydown.space.prevent="toggleCompareList"
        >
          <span
            class="compare-list__show-icon"
            :class="{ 'is-open': showCompareList }"
          >
            <IconArrow
              :class="showCompareList ? 'icon icon--arrow-down' : 'icon icon--arrow-up'"
            />
          </span>
          <span>{{ currentList.name }} {{ $t('To compare') }} <span class="compare-list__item-count">{{ (compareProductList as { item_count?: number })?.item_count || 0 }}</span></span>
        </button>
        <ul
          v-if="showCompareList"
          class="compare-list__items"
        >
          <li
            v-for="item in (compareProductList as { items?: any[] })?.items || []"
            :key="item.uid"
            class="compare-list__item"
          >
            <ProductCompareMiniTile :item="item" />
            <button
              class="compare-list__delete-btn"
              :aria-label="$t('Remove from comparison')"
              @click="removeProductsFromCompareList(item.uid)"
            >
              <IconTrash class="icon icon-trash" />
            </button>
          </li>
        </ul>
      </div>
      <AppLink
        v-if="showCompareList"
        class="compare-cta__button button--shop-flow button--full button--center button"
        :to="`/compare/?list=${currentList.uid}`"
      >
        <span>{{ $t('Show comparison') }}</span>
      </AppLink>
    </div>
    <AppLink
      v-if="currentList && ((compareProductList as { item_count?: number })?.item_count || 0) > 0"
      class="compare-cta__button  compare-cta__button--mobile button--shop-flow button--full button--center button"
      :to="`/compare/?list=${currentList.uid}`"
    >
      <span>{{ $t('Compare') }} <span class="compare-list__item-count">{{ (compareProductList as { item_count?: number })?.item_count || 0 }}</span></span>
    </AppLink>
  </div>
</template>

<script setup lang="ts">
const { compareProductList, currentList, removeProductsFromCompareList } = useCompareProductList()

const showCompareList = ref(false)
const compareCta = ref<HTMLElement>()
const previousItemCount = ref(0)

function toggleCompareList() {
  showCompareList.value = !showCompareList.value

  if (import.meta.client) {
    if (showCompareList.value) {
      document.body.classList.add('compare-list-open')
    }
    else {
      document.body.classList.remove('compare-list-open')
    }
  }
}

function closeCompareList() {
  showCompareList.value = false
  if (import.meta.client) {
    document.body.classList.remove('compare-list-open')
  }
}

function handleClickOutside(event: Event) {
  if (showCompareList.value && compareCta.value && !compareCta.value.contains(event.target as Node)) {
    closeCompareList()
  }
}

// Watch for compare list changes - cleanup when empty and trigger bounce
watch(() => (compareProductList.value as { item_count?: number })?.item_count || 0, (newCount) => {
  const currentCount = newCount || 0

  if (import.meta.client && currentCount !== previousItemCount.value && previousItemCount.value > 0 && compareCta.value) {
    compareCta.value.classList.remove('bounce')
    void compareCta.value.offsetHeight // Force reflow
    compareCta.value.classList.add('bounce')

    setTimeout(() => {
      if (compareCta.value) {
        compareCta.value.classList.remove('bounce')
      }
    }, 600)
  }

  previousItemCount.value = currentCount

  if (currentCount <= 1) {
    if (import.meta.client) {
      document.body.classList.remove('compare-list-open')
    }
    showCompareList.value = false
  }
}, { immediate: true })

onMounted(() => {
  if (import.meta.client) {
    document.addEventListener('click', handleClickOutside)
  }
})

onUnmounted(() => {
  if (import.meta.client) {
    document.removeEventListener('click', handleClickOutside)
    document.body.classList.remove('compare-list-open')
  }
})
</script>

<style>
@import url("~layers/app/components/Product/CompareCta.css");
</style>
